import { Frequency } from '@generated/graphql';
import { DEPOSIT_TRANSFER_TYPE } from '@ovComponents/4-module/workflowCompletion/constants/deposits';
import type { TransferContextData } from '@ovComponents/4-module/workflowCompletion/workflowTypes';
import type { ActiveOrganizationProps } from '@providers/userContextProvider';
import { dollars2cents } from '@utils/dollars2cents';
import type { TFunction } from 'i18next';
import { isNull } from 'lodash-es';

type DepositValidationError = {
  field: 'amountCents' | 'schedule';
  message: string;
};

type DepositMinimums = Pick<
  ActiveOrganizationProps,
  'minRecurringDepositCents' | 'minDepositCents' | 'minInitialDepositCents'
>;

type MinimumDepositRule = {
  minimumCents?: number;
  key:
    | 'components:transferModal.minRecurringDepositAmount'
    | 'components:transferModal.minInitialDeposit'
    | 'components:transferModal.minAmountDeposit';
};

const getMinimumDepositRule = ({
  isRecurringDeposit,
  transfersCount,
  organization,
}: {
  isRecurringDeposit: boolean;
  transfersCount: number | null;
  organization: DepositMinimums;
}): MinimumDepositRule | undefined => {
  if (isRecurringDeposit) {
    return {
      minimumCents: organization.minRecurringDepositCents,
      key: 'components:transferModal.minRecurringDepositAmount',
    };
  }
  if (isNull(transfersCount)) return;
  if (transfersCount === 0) {
    return {
      minimumCents: organization.minInitialDepositCents,
      key: 'components:transferModal.minInitialDeposit',
    };
  }
  return {
    minimumCents: organization.minDepositCents,
    key: 'components:transferModal.minAmountDeposit',
  };
};

export const toAmountCents = (amount?: string | number | null) =>
  amount ? dollars2cents(amount, 2) : undefined;

export const isNearFullWithdrawal = (
  deposit?: Pick<TransferContextData, 'amountCents' | 'fromSubAccount'>,
) =>
  (deposit?.fromSubAccount?.statistics?.marketValueCents ?? 0) > 0 &&
  (deposit?.amountCents || 0) >
    (deposit?.fromSubAccount?.statistics?.marketValueCents || 0) * 0.97;

export const getDepositValidationError = ({
  applicableData,
  options,
  transfersCount,
  organization,
  t,
}: {
  applicableData?: Pick<
    TransferContextData,
    'amountCents' | 'type' | 'schedule'
  > | null;
  options?: { schedule?: { enabled?: boolean } | null } | null;
  transfersCount: number | null;
  organization: DepositMinimums;
  t: TFunction;
}): DepositValidationError | undefined => {
  const amountCents = applicableData?.amountCents || 0;

  if (amountCents <= 0) {
    return {
      field: 'amountCents',
      message: t('components:transferModal.depositGreaterThanZero'),
    };
  }

  if (applicableData?.type !== DEPOSIT_TRANSFER_TYPE.EFT) return;

  const { schedule } = applicableData;

  if (!schedule && options?.schedule?.enabled) {
    return {
      field: 'schedule',
      message: t('components:transferModal.selectScheduleFirst'),
    };
  }

  const isRecurringDeposit = !!schedule && schedule !== Frequency.OneTime;
  const minimumRule = getMinimumDepositRule({
    isRecurringDeposit,
    transfersCount,
    organization,
  });

  if (!minimumRule?.minimumCents || amountCents >= minimumRule.minimumCents) {
    return;
  }

  return {
    field: 'amountCents',
    message: `${t(minimumRule.key)} $${minimumRule.minimumCents / 100}`,
  };
};
